// src/pages/UserManagementPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import UserModal from '../components/UserModal';
import { toast } from 'react-toastify';
import './UserManagementPage.css';

const API_URL = '/api/users';

function UserManagementPage() {
  const { authFetch, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch(API_URL);
      if (!response.ok) {
        throw new Error('Gagal mengambil data user');
      }
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Gagal memuat data user');
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (user) => {
    setIsEditing(true);
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
  };

  const handleSave = async (formData) => {
    try {
      let response;
      
      if (isEditing) {
        // Update user
        const updateData = {
          email: formData.email,
          role: formData.role
        };

        response = await authFetch(`${API_URL}/${selectedUser.id}`, {
          method: 'PUT',
          body: JSON.stringify(updateData)
        });

        // If password is provided, update it separately
        if (formData.password) {
          const passwordResponse = await authFetch(`${API_URL}/${selectedUser.id}/change-password`, {
            method: 'PUT',
            body: JSON.stringify({ password: formData.password })
          });

          if (!passwordResponse.ok) {
            const errorData = await passwordResponse.json();
            throw new Error(errorData.error || 'Gagal mengubah password');
          }
        }
      } else {
        // Create new user
        response = await authFetch(API_URL, {
          method: 'POST',
          body: JSON.stringify(formData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Gagal menyimpan user');
      }

      toast.success(`User berhasil ${isEditing ? 'diperbarui' : 'ditambahkan'}!`);
      handleCloseModal();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user:', error);
      toast.error(error.message || 'Gagal menyimpan user');
    }
  };

  const handleDelete = async (user) => {
    // Prevent deleting current user
    if (currentUser && user.email === currentUser.email) {
      toast.error('Anda tidak dapat menghapus akun Anda sendiri');
      return;
    }

    if (window.confirm(`Apakah Anda yakin ingin menghapus user "${user.email}"?`)) {
      try {
        const response = await authFetch(`${API_URL}/${user.id}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Gagal menghapus user');
        }

        toast.success('User berhasil dihapus!');
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        toast.error(error.message || 'Gagal menghapus user');
      }
    }
  };

  const handleChangePassword = async (user) => {
    const newPassword = prompt(`Masukkan password baru untuk ${user.email}:`);
    
    if (newPassword === null) {
      return; // User cancelled
    }

    if (!newPassword || newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }

    try {
      const response = await authFetch(`${API_URL}/${user.id}/change-password`, {
        method: 'PUT',
        body: JSON.stringify({ password: newPassword })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Gagal mengubah password');
      }

      toast.success('Password berhasil diubah!');
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message || 'Gagal mengubah password');
    }
  };

  const getRoleBadgeClass = (role) => {
    return role === 'admin' ? 'role-badge admin' : 'role-badge viewer';
  };

  return (
    <div className="user-management-page">
      <div className="dashboard-header">
        <h1>Manajemen User</h1>
        <button onClick={handleOpenAddModal} className="add-new-btn">
          Tambah User
        </button>
      </div>

      {loading ? (
        <div className="loading-container">
          <p>Memuat data user...</p>
        </div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <p>Belum ada user terdaftar</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="user-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td>
                    {user.email}
                    {currentUser && user.email === currentUser.email && (
                      <span className="current-user-badge"> (Anda)</span>
                    )}
                  </td>
                  <td>
                    <span className={getRoleBadgeClass(user.role)}>
                      {user.role}
                    </span>
                  </td>
                  <td className="actions-cell">
                    <button 
                      onClick={() => handleOpenEditModal(user)}
                      className="edit-btn"
                    >
                      Edit
                    </button>
                    <button 
                      onClick={() => handleChangePassword(user)}
                      className="password-btn"
                    >
                      Ubah Password
                    </button>
                    <button 
                      onClick={() => handleDelete(user)}
                      className="delete-btn"
                      disabled={currentUser && user.email === currentUser.email}
                    >
                      Hapus
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <UserModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        user={selectedUser}
        isEditing={isEditing}
      />
    </div>
  );
}

export default UserManagementPage;
